import React from 'react'
import { listAllFaqs, deleteFaq, type Faq } from '../services/faqService'
import FaqFormModal from '../components/FaqFormModal'
import { Table, Tr, Th, Td } from '@/shared/ui/Table'
import { Button, Input, Badge } from '@/shared/ui'
import { DropdownMenu } from '@/shared/ui/DropdownMenu'
import { MoreVertical, Search } from 'lucide-react'
import { filterFaqs } from '../utils/filterFaqs'

export default function FaqAdminPage() {
  const [items, setItems] = React.useState<Faq[]>([])
  const [loading, setLoading] = React.useState(true)
  const [error, setError] = React.useState<string | null>(null)
  const [openForm, setOpenForm] = React.useState(false)
  const [current, setCurrent] = React.useState<Faq | null>(null)
  const [q, setQ] = React.useState('')

  const load = React.useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const list = await listAllFaqs()
      setItems(list)
    } catch (e: any) {
      setError(e?.message ?? 'Falha ao carregar FAQs')
    } finally {
      setLoading(false)
    }
  }, [])

  React.useEffect(() => { load() }, [load])

  function onCreate() { setCurrent(null); setOpenForm(true) }
  function onEdit(t: Faq) { setCurrent(t); setOpenForm(true) }
  async function onDelete(t: Faq) {
    if (!confirm('Excluir FAQ?')) return
    try { await deleteFaq(t.id); await load() } catch (e: any) { setError(e?.message ?? 'Falha ao excluir') }
  }

  const filtered = React.useMemo(
    () => filterFaqs(items, q),
    [items, q],
  )

  return (
    <div className="mx-auto w-full max-w-[1550px]">
      <FaqFormModal open={openForm} onClose={()=> setOpenForm(false)} onSaved={load} initial={current ?? undefined} />
      <div className="mb-6">
        <div className="text-2xl font-semibold">FAQ</div>
        <div className="text-sm text-foreground/70">Gerencie as dúvidas exibidas na Home.</div>
      </div>
      {error && <div className="mb-4 rounded-md border border-destructive/30 bg-destructive/10 p-2 text-sm text-destructive-foreground">{error}</div>}
      <div className="mb-4 flex items-center justify-between gap-3">
        <div className="relative w-full max-w-md">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-foreground/60" />
          <Input value={q} onChange={(e)=> setQ(e.target.value)} placeholder="Buscar dúvidas" className="pl-9" />
        </div>
        <Button onClick={onCreate}>Nova FAQ</Button>
      </div>
      <Table>
        <thead>
          <Tr>
            <Th>Título</Th>
            <Th>Texto</Th>
            <Th>Posição</Th>
            <Th>Visível</Th>
            <Th className="w-12 text-right">Ações</Th>
          </Tr>
        </thead>
        <tbody>
          {filtered.map((t) => (
            <Tr key={t.id}>
              <Td className="max-w-[260px]">
                <div className="text-sm font-medium truncate">{t.title}</div>
              </Td>
              <Td className="text-sm text-foreground/80 truncate max-w-[540px]">{t.content}</Td>
              <Td className="text-sm">{typeof t.position === 'number' ? t.position : '-'}</Td>
              <Td>{(t as any).visible !== false ? <Badge variant="soft" color="success">Sim</Badge> : <Badge variant="soft" color="error">Não</Badge>}</Td>
              <Td className="text-right">
                <DropdownMenu
                  align="end"
                  trigger={<Button variant="outline" className="h-8 w-8 p-0"><MoreVertical className="h-4 w-4" /></Button>}
                  items={[
                    { label: 'Editar', onSelect: () => onEdit(t) },
                    { label: <span className="text-destructive">Excluir</span>, onSelect: () => onDelete(t) },
                  ]}
                />
              </Td>
            </Tr>
          ))}
          {filtered.length === 0 && (
            <Tr>
              <Td colSpan={5} className="py-10 text-center text-sm text-foreground/70">Nenhuma FAQ encontrada.</Td>
            </Tr>
          )}
        </tbody>
      </Table>
    </div>
  )
}
